import { ApolloError } from '@apollo/client/errors';
import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { DatePicker, LoadingPage, Modal, Select, TextField } from '@chocolate-soup-inc/cs-frontend-components';
import { format } from 'date-fns';
import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryListAllCompaniesOptions } from '../../entities/companies/queries';
import { useQueryAllEmployeesOld } from '../../entities/employees/queries';
import { getEventDefaultValues } from '../../entities/events/schema';
import { ONE_OFF_EVENT_TYPES } from '../../entities/events/shared';
import { TEventTypes, useCreateOneOffEventMutation } from '../../generated/graphql';
import styles from './Events.module.scss';

export const EventCreateForm = () => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>();
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[] | undefined>();
  const [typeSelect, setTypeSelect] = useState<TEventTypes | undefined>();
  const [typeText, setTypeText] = useState<string | undefined>();
  const [selectedEventDate, setSelectedEventDate] = useState<Date | undefined>(new Date());
  const [notesText, setNotesText] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);

  const navigate = useNavigate();
  const [createOneOffEvent] = useCreateOneOffEventMutation();

  const { data: companies, loading: companiesLoading } = useQueryListAllCompaniesOptions();
  const { data: employees, loading: employeesLoading } = useQueryAllEmployeesOld();

  const companiesList = useMemo(
    () =>
      _.orderBy(
        companies
          ?.filter((company) => {
            const hasReachedEndDate = company.endDate && readableDate(company.endDate, 'yyyy-MM-dd') < readableDate(new Date(), 'yyyy-MM-dd');
            return company.active && company.isLive && !hasReachedEndDate;
          })
          .map((company) => ({ label: company.name, value: company.id })),
        ['label'],
        ['asc']
      ),
    [companies]
  );
  const employeesList = useMemo(
    () =>
      _.orderBy(
        employees.filter((employee) => employee?.companyId === selectedCompanyId && employee?.active).map((employee) => ({ label: employee?.fullName as string, value: employee?.id as string })),
        ['label'],
        ['asc']
      ),
    [employees, selectedCompanyId]
  );
  const fullSelectedCompany = useMemo(() => companies?.find((company) => company.id === selectedCompanyId), [companies, selectedCompanyId]);

  const closeModal = useCallback(() => {
    navigate(generatePath(`../`));
  }, [navigate]);

  const onSubmit = useCallback(async () => {
    if (creating || !selectedCompanyId || !selectedEmployeeIds || !selectedEventDate || !typeSelect) return;
    setCreating(true);
    let failed = false;
    try {
      // Finish the selected employees before closing the form.
      for (const employeeId of selectedEmployeeIds) {
        const fullSelectedEmployee = employees.find((employee) => employee?.id === employeeId);
        const input = {
          ...getEventDefaultValues(),
          companyId: selectedCompanyId,
          companyDeliveryMethod: fullSelectedCompany?.deliveryMethod,
          employeeId: fullSelectedEmployee?.id,
          employeeOfficeId: fullSelectedEmployee?.officeId,
          eventDate: format(selectedEventDate, 'yyyy-MM-dd'),
          type: typeSelect,
          customType: typeText,
          notes: notesText,
          recipientId: `${import.meta.env.REACT_APP_EMPLOYEES_TABLE_NAME}|${fullSelectedEmployee?.id}`,
          referenceEventId: null
        };
        try {
          await createOneOffEvent({ variables: { input: JSON.stringify(input) } });
          toast.success(`Successfully created the event for ${fullSelectedEmployee?.fullName}.`);
        } catch (error) {
          failed = true;
          toast.error(`There was an error creating the event for ${fullSelectedEmployee?.fullName}.`);
          if (error instanceof ApolloError) for (const graphQLError of error.graphQLErrors) toast.error(graphQLError.message);
        }
      }
      if (!failed) closeModal();
    } finally {
      setCreating(false);
    }
  }, [creating, closeModal, createOneOffEvent, selectedEmployeeIds, typeSelect, typeText, notesText, selectedCompanyId, selectedEventDate, employees, fullSelectedCompany?.deliveryMethod]);

  if (companiesLoading || employeesLoading)
    return (
      <Modal closeModal={closeModal} headline='New One-Off Event'>
        <LoadingPage />
      </Modal>
    );

  return (
    <Modal
      closeModal={closeModal}
      confirmLabel='Create'
      confirmLoading={creating}
      headline='New One-Off Event'
      onConfirmClick={onSubmit}
      confirmDisabled={!selectedEmployeeIds || !typeSelect || ((typeSelect === TEventTypes.Other || typeSelect === TEventTypes.CustomBricks) && !typeText) || !selectedEventDate}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      showConfirmButton={true}
      size='large'>
      <p className={styles.supportText}>Select the Employee whose event is going to be created.</p>
      <div className={styles.filters}>
        <Select
          className={styles.filter}
          label='Company'
          multiple={false}
          name='companyId'
          onChange={(v) => {
            setSelectedCompanyId(v as string);
            setSelectedEmployeeIds(undefined);
          }}
          options={companiesList}
          value={selectedCompanyId}
          variant='outlined'
        />
        <Select
          className={styles.filter}
          label='Employee'
          multiple={true}
          disabled={!selectedCompanyId}
          name='employeeId'
          onChange={(v) => {
            setSelectedEmployeeIds(v as string[]);
          }}
          options={employeesList}
          value={selectedEmployeeIds}
          variant='outlined'
        />
      </div>
      <div className={styles.filters}>
        <Select
          className={styles.filter}
          label='Event Type'
          multiple={false}
          name='type'
          onChange={(v) => {
            setTypeSelect(v as TEventTypes);
            setTypeText(undefined);
          }}
          options={Object.entries(ONE_OFF_EVENT_TYPES).map(([key, value]) => ({ label: value, value: key }))}
          value={typeSelect}
          variant='outlined'
        />
        {(typeSelect === TEventTypes.Other || typeSelect === TEventTypes.CustomBricks) && (
          <TextField
            className={styles.filter}
            label={typeSelect === TEventTypes.CustomBricks ? 'Custom Bricks Description' : 'Custom Event Type'}
            multiline={false}
            name='typeCustom'
            onChange={(v) => {
              setTypeText(v != null ? (v as string) : undefined);
            }}
            value={typeText}
            variant='outlined'
          />
        )}
      </div>
      <div className={styles.filters}>
        <DatePicker
          label='Event Date'
          className={styles.filter}
          name='eventDate'
          onChange={(v) => {
            setSelectedEventDate(v != null ? (v as Date) : undefined);
          }}
          value={selectedEventDate}
          variant='outlined'
        />
        <TextField
          className={styles.filter}
          label='Custom Event Notes'
          multiline={false}
          name='notes'
          onChange={(v) => {
            setNotesText(v != null ? (v as string) : undefined);
          }}
          value={notesText}
          variant='outlined'
        />
      </div>
      <div className={styles.filters}></div>
    </Modal>
  );
};
